// Hangman: guess the hidden word a letter at a time; six wrong guesses and the drawing is complete. The words are
// the word search themes' lists (public/puzzles/hangman/all.json), and the theme is shown as the clue. Pure
// functions over a plain state; the interface is hangman/ui.ts.
export interface HangmanWord { t: string; w: string }
export interface HangmanState {
  word: string // upper case A–Z
  theme: string
  guesses: string[] // letters, in the order tried; hint letters count as guesses (right ones)
  hints: number
  seed: string
  attempt?: number
}
export const MAX_WRONG = 6
export const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")

export const start = (w: HangmanWord, seed: string): HangmanState => ({ word: w.w.toUpperCase(), theme: w.t, guesses: [], hints: 0, seed })
export const wrongLetters = (s: HangmanState): string[] => s.guesses.filter((g) => !s.word.includes(g))
export const wrong = (s: HangmanState): number => wrongLetters(s).length
export const isWon = (s: HangmanState): boolean => [...new Set(s.word)].every((l) => s.guesses.includes(l))
export const isLost = (s: HangmanState): boolean => !isWon(s) && wrong(s) >= MAX_WRONG
export const isOver = (s: HangmanState): boolean => isWon(s) || isLost(s)

/** The word as shown: each letter, or "" where it is still hidden. */
export const masked = (s: HangmanState): string[] => s.word.split("").map((l) => (s.guesses.includes(l) ? l : ""))

export function guess(s: HangmanState, letter: string): HangmanState {
  const l = letter.toUpperCase()
  if (isOver(s) || !LETTERS.includes(l) || s.guesses.includes(l)) return s
  return { ...s, guesses: [...s.guesses, l] }
}

/** A letter to reveal: the hidden letter that appears most often in the word (ties: the earliest), or null. */
export function hintLetter(s: HangmanState): string | null {
  if (isOver(s)) return null
  const hidden = s.word.split("").filter((l) => !s.guesses.includes(l))
  if (!hidden.length) return null
  const count = new Map<string, number>()
  for (const l of hidden) count.set(l, (count.get(l) ?? 0) + 1)
  return [...count.entries()].sort((a, b) => b[1] - a[1])[0]![0]
}
export const reveal = (s: HangmanState): HangmanState => {
  const l = hintLetter(s)
  return l ? { ...guess(s, l), hints: s.hints + 1 } : s
}
